import React, { useEffect, useState } from "react";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell
} from "recharts";
import AnalyticsService from "../../../services/analytics.service";
import "../../../styles/analytics-dashboard.css";
import { FaEye, FaUsers, FaMousePointer, FaChartLine, FaHeart, FaInfoCircle } from "react-icons/fa";

const AdAnalyticsDashboard = ({ sellerId, productId, onClearProduct }) => {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (sellerId) {
            fetchAnalytics();
        }
    }, [sellerId, productId]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            const res = productId
                ? await AnalyticsService.getProductAnalytics(productId)
                : await AnalyticsService.getSellerAnalytics(sellerId);

            if (res.data.status) {
                setAnalyticsData(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching analytics:", err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading Analytics...</span>
                </div>
                <p className="mt-3 text-muted">Analyzing your ad performance...</p>
            </div>
        );
    }

    if (!analyticsData) {
        return (
            <div className="text-center py-5 shadow-sm rounded bg-white mt-4">
                <div className="mb-3"><FaChartLine size={48} color="#cbd5e0" /></div>
                <h4 className="text-muted">No analytics data available yet</h4>
                <p className="text-muted small">Once your ads get views or engagement, insights will appear here.</p>
            </div>
        );
    }

    // Engagement Colors for Modern UI
    const engagementColors = {
        'Ad Views': '#3182ce',
        'Favorites': '#e53e3e',
        'Clicks': '#38a169',
        'Contacts': '#dd6b20'
    };

    const actionLabels = {
        'VIEW': 'Ad Views',
        'SAVE': 'Favorites',
        'SAVE_PRODUCT': 'Favorites',
        'CLICK': 'Clicks',
        'CONTACT': 'Contacts'
    };

    const engagementData = (analyticsData.engagement || []).map((item) => {
        const name = actionLabels[item.action] || item.action;
        return {
            name: name,
            value: item.count,
            fill: engagementColors[name] || "#718096"
        };
    });

    const totalEngagement = (analyticsData.engagement || []).reduce((acc, curr) => acc + curr.count, 0);

    return (
        <div className="analytics-dashboard animate__animated animate__fadeIn">
            <div className="analytics-section-header d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm">
                <div className="d-flex align-items-center">
                    <div className="p-2 rounded-circle bg-light me-3">
                        <FaChartLine size={24} color="#3182ce" />
                    </div>
                    <div>
                        <h2 className="h4 mb-0 fw-bold">
                            {productId ? "Ad Insights" : "Marketplace Ad Performance"}
                        </h2>
                        <p className="small text-muted mb-0">Track engagement and conversion trends</p>
                    </div>
                </div>
                {productId && (
                    <button
                        className="btn btn-sm btn-outline-primary d-flex align-items-center px-3"
                        onClick={onClearProduct}
                        style={{ borderRadius: '20px' }}
                    >
                        &larr; <span className="ms-1">Back to All Ads</span>
                    </button>
                )}
            </div>

            {/* Stats Summary Cards */}
            <div className="stats-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                <div className="stat-card shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #fff 0%, #f7fafc 100%)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="stat-label text-muted small fw-bold mb-1">TOTAL AD VIEWS</div>
                            <div className="stat-value h2 fw-bold mb-0" style={{ color: '#2b6cb0' }}>{analyticsData.totalViews.toLocaleString()}</div>
                        </div>
                        <div className="p-2 bg-light rounded-circle"><FaEye color="#3182ce" /></div>
                    </div>
                </div>
                <div className="stat-card shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #fff 0%, #f0fff4 100%)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="stat-label text-muted small fw-bold mb-1">UNIQUE VISITORS</div>
                            <div className="stat-value h2 fw-bold mb-0" style={{ color: '#2f855a' }}>
                                {(analyticsData.totalUniqueVisitors || analyticsData.uniqueVisitors || 0).toLocaleString()}
                            </div>
                        </div>
                        <div className="p-2 bg-light rounded-circle"><FaUsers color="#38a169" /></div>
                    </div>
                </div>
                <div className="stat-card shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #fff 0%, #fff5f5 100%)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="stat-label text-muted small fw-bold mb-1">FAVORITES</div>
                            <div className="stat-value h2 fw-bold mb-0" style={{ color: '#c53030' }}>
                                {(analyticsData.totalFavorites || 0).toLocaleString()}
                            </div>
                            {analyticsData.interestRatio > 0 && (
                                <div className="mt-1">
                                    <span className={`badge rounded-pill ${analyticsData.interestRatio > 10 ? 'bg-danger' : 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                                        {analyticsData.interestRatio}% Interest Ratio
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="p-2 bg-light rounded-circle"><FaHeart color="#e53e3e" /></div>
                    </div>
                </div>
                <div className="stat-card shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #fff 0%, #fffaf0 100%)', borderRadius: '12px', padding: '1.25rem' }}>
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <div className="stat-label text-muted small fw-bold mb-1">TOTAL ACTIONS</div>
                            <div className="stat-value h2 fw-bold mb-0" style={{ color: '#9c4221' }}>{totalEngagement.toLocaleString()}</div>
                        </div>
                        <div className="p-2 bg-light rounded-circle"><FaMousePointer color="#dd6b20" /></div>
                    </div>
                </div>
            </div>

            <div className="row g-4 mb-4">
                {/* Views Trend - Area Chart with Gradient */}
                <div className="col-lg-8">
                    <div className="chart-card bg-white p-4 rounded shadow-sm h-100">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="fw-bold mb-0 h5">Views Trend (Daily)</h4>
                            <div className="badge bg-light text-primary border rounded-pill px-3 py-2 fw-normal">Performance Flow</div>
                        </div>
                        <div style={{ height: '350px', width: '100%' }}>
                            {analyticsData.dailyViews && analyticsData.dailyViews.length > 1 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={analyticsData.dailyViews}>
                                        <defs>
                                            <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3182ce" stopOpacity={0.15} />
                                                <stop offset="95%" stopColor="#3182ce" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf2f7" />
                                        <XAxis
                                            dataKey="date"
                                            axisLine={false}
                                            tickLine={false}
                                            stroke="#a0aec0"
                                            fontSize={12}
                                            tick={{ dy: 10 }}
                                        />
                                        <YAxis
                                            axisLine={false}
                                            tickLine={false}
                                            stroke="#a0aec0"
                                            fontSize={12}
                                        />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                            labelStyle={{ fontWeight: 'bold', color: '#4a5568' }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="count"
                                            stroke="#3182ce"
                                            strokeWidth={3}
                                            fillOpacity={1}
                                            fill="url(#colorViews)"
                                            dot={{ r: 4, fill: '#3182ce', strokeWidth: 2, stroke: '#fff' }}
                                            activeDot={{ r: 6, strokeWidth: 0 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-100 d-flex flex-column justify-content-center align-items-center text-muted bg-light rounded border-dashed" style={{ border: '2px dashed #e2e8f0' }}>
                                    <FaInfoCircle size={32} className="mb-2 opacity-50" />
                                    <p className="mb-0 fw-bold">Not enough data to display trend</p>
                                    <small>Views trend will appear once you have more activity</small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Engagement Breakdown - Colored Bar Chart */}
                <div className="col-lg-4">
                    <div className="chart-card bg-white p-4 rounded shadow-sm h-100">
                        <h4 className="fw-bold mb-4 h5">User Engagement Breakdown</h4>
                        <div style={{ height: '350px', width: '100%' }}>
                            {engagementData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={engagementData} layout="vertical" margin={{ left: 10, right: 30 }}>
                                        <XAxis type="number" hide />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            axisLine={false}
                                            tickLine={false}
                                            stroke="#4a5568"
                                            fontSize={12}
                                            width={80}
                                        />
                                        <Tooltip
                                            cursor={{ fill: '#f7fafc' }}
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                        />
                                        <Bar
                                            dataKey="value"
                                            radius={[0, 10, 10, 0]}
                                            barSize={18}
                                        >
                                            {engagementData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-100 d-flex justify-content-center align-items-center text-muted bg-light rounded" style={{ border: '2px dashed #e2e8f0' }}>
                                    <p>No engagement actions yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdAnalyticsDashboard;
